import { useEffect, useRef, useState } from 'react';

import {
  LocationSuggestion,
  getLocationSuggestions,
} from 'lib/api/getLocationSuggestions';

// Require at least 2 characters to avoid broad, poorly ranked results
export function canSuggestLocation(query: string): boolean {
  return query.trim().length >= 2;
}

interface UseLocationSuggestionsResult {
  suggestions: LocationSuggestion[];
  isLoading: boolean;
}

export function useLocationSuggestions(
  query: string,
  limit = 5
): UseLocationSuggestionsResult {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestSequence = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    const sequence = ++requestSequence.current;

    if (!canSuggestLocation(trimmed)) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    // Match the visible debounce window: feedback starts as soon as a query is
    // eligible, while the network request still waits 300 ms.
    setSuggestions([]);
    setIsLoading(true);

    const timer = window.setTimeout(async () => {
      const result = await getLocationSuggestions(trimmed, limit);

      if (sequence !== requestSequence.current) {
        return;
      }

      setSuggestions(result.isOk() ? result.value.suggestions : []);
      setIsLoading(false);
    }, 300);

    return () => {
      window.clearTimeout(timer);
      if (requestSequence.current === sequence) {
        requestSequence.current += 1;
      }
    };
  }, [limit, query]);

  return { suggestions, isLoading };
}
